import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, Search, X, Upload, ChevronLeft, ChevronRight, Trash2, Cloud, CloudOff, Loader2, Fence } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation as useGpsLocation } from '../../contexts/LocationContext';
import { JobSelector } from '../common/JobSelector';
import { Job } from '../../types/backend.types';
import { uploadQueueService } from '../../services/upload-queue.service';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS, isNativeApp } from '../../utils/platform.utils';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

interface Photo {
  photo_id: number;
  photo_url: string;
  thumbnail_url?: string | null;
  caption?: string;
  created_at: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
  report_date: string;
  source?: 'daily' | 'asphalt';
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const PhotoGallery: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { location: gpsLocation, ensureFreshLocation } = useGpsLocation();

  // Navigate with location refresh (matches dashboard pattern)
  const navigateToTimeClock = async () => {
    await ensureFreshLocation();
    navigate('/time-clock');
  };
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });
  const [user, setUser] = useState<any>(null);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [laborCode, setLaborCode] = useState<string | null>(null);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [queueStats, setQueueStats] = useState({ pending: 0, uploading: 0, failed: 0, completed: 0, total: 0 });
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [siltFenceMode, setSiltFenceMode] = useState(false); // Flag for silt fence photo capture
  const [newPhotosCount, setNewPhotosCount] = useState(0); // Track photos taken but not yet refreshed in gallery
  const [imageLoading, setImageLoading] = useState(false); // Loading state for fullscreen image

  // Loading states
  const [loadingStates, setLoadingStates] = useState({
    user: true,
    jobs: true,
    currentJob: true,
    initializing: true
  });

  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

  // Track if component has completed initial mount (to avoid redirect race condition)
  const [hasCompletedMount, setHasCompletedMount] = useState(false);

  // Mark component as mounted after a short delay to let location context propagate
  useEffect(() => {
    const timer = setTimeout(() => setHasCompletedMount(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Listen for page visibility changes (iPad sleep/wake, tab switch) - redirect on wake if stale
  // Admins are exempt — they don't need location
  useEffect(() => {
    if (isNativeApp()) return; // Native apps handle this in LocationContext
    if (isAdmin) return; // Admins don't need location

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('PhotoGallery: Page became visible, checking location staleness');
        // Check staleness directly here to avoid race conditions
        if (!gpsLocation || (Date.now() - gpsLocation.timestamp) > 5 * 60 * 1000) {
          console.log('PhotoGallery: Location stale after wake, redirecting to dashboard');
          navigate('/');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [gpsLocation, navigate]);

  // Check for stale location (> 5 minutes old) - PWA only, redirect to dashboard
  // Only redirect after mount completes to avoid race with location context update
  // Admins are exempt — they don't need location to use the photo gallery
  const isAdmin = auth?.user?.role === 'admin';
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  const isLocationStale = useMemo(() => {
    if (isAdmin) return false; // Admins don't need location
    if (isNativeApp()) return false; // Native apps auto-refresh on resume
    if (!gpsLocation) return true;
    return (Date.now() - gpsLocation.timestamp) > STALE_THRESHOLD;
  }, [gpsLocation, isAdmin]);

  // Redirect to dashboard if location is stale on PWA (no form data to preserve)
  // Wait for mount to complete to avoid race condition with location context update
  useEffect(() => {
    if (isLocationStale && !isNativeApp() && !loadingStates.initializing && hasCompletedMount) {
      console.log('PhotoGallery: Location stale on mount check, redirecting to dashboard');
      navigate('/');
    }
  }, [isLocationStale, loadingStates.initializing, hasCompletedMount, navigate]);

  // Note: Job mismatch detection is handled globally by GlobalMismatchChecker in App.tsx

  // Translations
  const translations = {
    en: {
      title: 'Photo Gallery',
      pleaseClock: 'Please clock into a job to view photos',
      selectJob: 'Select a job',
      searchJobs: 'Search jobs...',
      photosSaved: 'Photos saved! Uploading in background...',
      uploading: 'Uploading',
      photo: 'photo',
      photos: 'photos',
      queued: 'queued',
      failed: 'failed',
      uploadAuto: 'Photos will upload automatically when online',
      saving: 'Saving...',
      takePhoto: 'Take Photo',
      upload: 'Upload',
      searchPlaceholder: 'Search by date or caption...',
      loadingPhotos: 'Loading photos...',
      noPhotos: 'No photos found',
      prev: 'Prev',
      page: 'Page',
      of: 'of',
      next: 'Next',
      delete: 'Delete',
      deleteTitle: 'Delete Photo?',
      deleteConfirm: 'Are you sure you want to delete this photo from',
      deleteWarning: 'This action cannot be undone.',
      cancel: 'Cancel',
      deletePhoto: 'Delete Photo',
      deleting: 'Deleting...',
      by: 'By',
      refreshGallery: 'Refresh Gallery',
      newPhotos: 'new photos',
      siltFencePhoto: 'Silt Fence Photo',
      siltFenceTag: 'Silt Fence'
    },
    es: {
      title: 'Galería de Fotos',
      pleaseClock: 'Por favor, registre entrada en un trabajo para ver fotos',
      selectJob: 'Seleccionar un trabajo',
      searchJobs: 'Buscar trabajos...',
      photosSaved: '¡Fotos guardadas! Subiendo en segundo plano...',
      uploading: 'Subiendo',
      photo: 'foto',
      photos: 'fotos',
      queued: 'en cola',
      failed: 'fallidas',
      uploadAuto: 'Las fotos se subirán automáticamente cuando esté en línea',
      saving: 'Guardando...',
      takePhoto: 'Tomar Foto',
      upload: 'Subir',
      searchPlaceholder: 'Buscar por fecha o descripción...',
      loadingPhotos: 'Cargando fotos...',
      noPhotos: 'No se encontraron fotos',
      prev: 'Anterior',
      page: 'Página',
      of: 'de',
      next: 'Siguiente',
      delete: 'Eliminar',
      deleteTitle: '¿Eliminar Foto?',
      deleteConfirm: '¿Está seguro de que desea eliminar esta foto de',
      deleteWarning: 'Esta acción no se puede deshacer.',
      cancel: 'Cancelar',
      deletePhoto: 'Eliminar Foto',
      deleting: 'Eliminando...',
      by: 'Por',
      refreshGallery: 'Actualizar Galería',
      newPhotos: 'fotos nuevas',
      siltFencePhoto: 'Foto de Cerca de Sedimentos',
      siltFenceTag: 'Cerca de Sedimentos'
    }
  };

  const t = translations[language];

  // Styles
  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '100px', // Extra padding for mobile nav and pagination visibility
      maxWidth: '100vw',
      minHeight: '100vh',
      backgroundColor: '#f8fafc'
    },
    header: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '16px',
      borderRadius: '12px 12px 0 0',
      marginBottom: '20px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '14px',
      opacity: 0.9
    },
    searchContainer: {
      marginBottom: '20px',
      position: 'relative' as const
    },
    searchInput: {
      width: '100%',
      padding: '12px 40px 12px 40px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white'
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    clearIcon: {
      position: 'absolute' as const,
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      cursor: 'pointer'
    },
    uploadSection: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      border: '2px dashed #d1d5db'
    },
    uploadButton: {
      flex: 1,
      padding: '12px',
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    cameraButton: {
      flex: 1,
      padding: '12px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px'
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '20px'
    },
    photoCard: {
      position: 'relative' as const,
      aspectRatio: '1',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s'
    },
    thumbnail: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const
    },
    photoInfo: {
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
      color: 'white',
      padding: '8px',
      fontSize: '12px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      marginTop: '32px',
      marginBottom: '40px',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    paginationButton: {
      padding: '12px 20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '16px',
      fontWeight: '600',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
      transition: 'all 0.2s'
    },
    paginationButtonDisabled: {
      padding: '12px 20px',
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      border: 'none',
      borderRadius: '8px',
      cursor: 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '16px',
      fontWeight: '600'
    },
    fab: {
      position: 'fixed' as const,
      right: '20px',
      bottom: '100px', // Above mobile nav bar (higher for Android)
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)',
      zIndex: 1000,
      transition: 'all 0.3s ease'
    },
    fabBadge: {
      position: 'absolute' as const,
      top: '-4px',
      right: '-4px',
      backgroundColor: '#ef4444',
      color: 'white',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      border: '2px solid white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    refreshButton: {
      marginTop: '20px',
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      margin: '20px auto',
      boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
    },
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '0'
    },
    modalImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
      imageOrientation: 'from-image' as any
    },
    modalInfo: {
      position: 'absolute' as const,
      bottom: '100px', // Higher for Android nav bar
      left: '20px',
      right: '20px',
      color: 'white',
      textAlign: 'center' as const,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      padding: '12px',
      borderRadius: '8px',
      backdropFilter: 'blur(4px)'
    },
    closeButton: {
      position: 'absolute' as const,
      top: '20px',
      right: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    },
    navButton: {
      position: 'absolute' as const,
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '48px',
      height: '48px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001,
      transition: 'background-color 0.2s'
    },
    navButtonLeft: {
      left: '20px'
    },
    navButtonRight: {
      right: '20px'
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6b7280'
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      border: '1px solid #6ee7b7',
      color: '#065f46',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      textAlign: 'center' as const
    },
    jobSelector: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      border: '2px solid #e5e7eb',
      position: 'relative' as const
    },
    jobSelectorText: {
      flex: 1,
      fontSize: '16px',
      fontWeight: '500',
      color: '#1e293b'
    },
    jobDropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      marginTop: '4px',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      zIndex: 10,
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    jobOption: {
      padding: '12px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background-color 0.2s'
    },
    jobSearchInput: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: 'none',
      borderBottom: '2px solid #e5e7eb',
      outline: 'none'
    },
    deleteButton: {
      position: 'absolute' as const,
      top: '70px',
      right: '20px',
      backgroundColor: 'rgba(239, 68, 68, 0.9)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      fontWeight: '600',
      zIndex: 1001
    },
    confirmModal: {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '400px',
      width: '90%',
      zIndex: 1002,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    confirmOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1001
    },
    confirmTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '12px',
      color: '#1e293b'
    },
    confirmText: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '20px',
      lineHeight: '1.5'
    },
    confirmButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    confirmButtonCancel: {
      padding: '10px 20px',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    confirmButtonDelete: {
      padding: '10px 20px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer'
    }
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es' | null;
    if (savedLanguage === 'en' || savedLanguage === 'es') {
      setLanguage(savedLanguage);
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load user
        const userData = localStorage.getItem('bfa_user');
        if (userData) {
          setUser(JSON.parse(userData));
          setLoadingStates(prev => ({ ...prev, user: false }));
        }

        // Load jobs
        const loadedJobs = await loadAllJobs();
        setLoadingStates(prev => ({ ...prev, jobs: false }));

        // Load current job with the loaded jobs list (avoid stale closure)
        if (userData) {
          await loadCurrentJob(JSON.parse(userData), loadedJobs);
        }

        // Start auto-sync for upload queue
        uploadQueueService.startAutoSync();

        // Update queue stats periodically
        const updateStats = async () => {
          const stats = await uploadQueueService.getQueueStats();
          setQueueStats(stats);
        };
        updateStats();
        const statsInterval = setInterval(updateStats, 5000); // Update every 5 seconds

        return () => {
          clearInterval(statsInterval);
          // Don't stop sync here - it's managed app-wide now
        };
      } catch (err) {
        console.error('Error initializing PhotoGallery:', err);
        setLoadingStates(prev => ({ ...prev, initializing: false }));
      }
    };

    initializeData();
  }, []);

  // Note: loadCurrentJob is now called directly in initialization with the loaded jobs list
  // to avoid stale closure issues with allJobs state

  useEffect(() => {
    if (currentJob) {
      loadPhotos();
      // Scroll to top when page changes (but not on initial load)
      if (page > 1 || searchTerm) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [currentJob, page, searchTerm]);

  const loadCurrentJob = async (userData?: any, jobsList?: Job[]) => {
    try {
      setLoadingStates(prev => ({ ...prev, currentJob: true }));

      // Use passed jobs list or fall back to state (may be stale)
      const availableJobs = jobsList || allJobs;

      // Check if job was passed via navigation state first
      if (location.state?.jobId) {
        console.log('✅ Job passed via navigation:', location.state, 'Available jobs:', availableJobs.length);
        // Find full job from jobs list
        const fullJob = availableJobs.find(j => j.job_id === location.state.jobId);
        if (fullJob) {
          console.log('✅ Found full job:', fullJob.job_name);
          setCurrentJob(fullJob);
        } else {
          console.log('⚠️ Job not found in list, using partial data');
          // Fallback to partial job data (type assertion needed)
          setCurrentJob({
            job_id: location.state.jobId,
            job_name: location.state.jobName || '',
            bradanna_job_number: location.state.jobNumber || ''
          } as Job);
        }
        setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
        return;
      }

      // Check if user is clocked in
      const response = await fetch(`${API_BASE_URL}/api/time/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.job_id) {
          // Store labor code for photo routing (ASPHALT vs regular)
          setLaborCode(data.data.labor_code || null);

          // Note: Job mismatch detection is handled globally by GlobalMismatchChecker

          // Fetch full job details to ensure we have correct job info
          const jobResponse = await fetch(`${API_BASE_URL}/api/jobs`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
            }
          });

          if (jobResponse.ok) {
            const jobData = await jobResponse.json();
            const fullJob = jobData.data.find((j: Job) => j.job_id === data.data.job_id);

            if (fullJob) {
              setCurrentJob(fullJob);
              setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
              return; // Found job from time entry
            } else {
              // Fallback to clock-in data if job not found (type assertion needed)
              setCurrentJob({
                job_id: data.data.job_id,
                job_name: data.data.job_name || '',
                bradanna_job_number: data.data.bradanna_job_number || ''
              } as Job);
              setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
              return;
            }
          }
        }
      }
      // Not clocked in
      setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
    } catch (error) {
      console.error('Error loading current job:', error);
      setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
    }
  };

  const loadAllJobs = async (): Promise<Job[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllJobs(data.data || []);
        return data.data || [];
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
    return [];
  };

  const loadPhotos = async () => {
    if (!currentJob) return;

    setLoading(true);
    try {
      console.log(`📸 Loading photos for job_id: ${currentJob.job_id}, job_name: "${currentJob.job_name}", bradanna_job_number: "${currentJob.bradanna_job_number || 'NULL'}"`);

      const params = new URLSearchParams({
        job_id: currentJob.job_id.toString(),
        page: page.toString(),
        limit: '50'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      console.log(`📸 Fetching: ${API_BASE_URL}/api/daily-reports/photos?${params}`);

      const response = await fetch(`${API_BASE_URL}/api/daily-reports/photos?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`📸 Received ${data.data.photos.length} photos for job_id ${currentJob.job_id}`);
        console.log(`📸 Pagination: page ${data.data.pagination.currentPage} of ${data.data.pagination.totalPages} (total: ${data.data.pagination.total} photos)`);
        setPhotos(data.data.photos);
        setTotalPages(data.data.pagination.totalPages || 1);
      } else {
        // Clear photos on error (don't show old photos from previous job)
        console.log(`📸 Error loading photos (${response.status}), clearing gallery`);
        setPhotos([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      // Clear photos on error (don't show old photos from previous job)
      setPhotos([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentJob || !e.target.files || e.target.files.length === 0 || !user) return;

    setUploading(true);
    try {
      const files = Array.from(e.target.files).map(file => ({
        file: file,
        filename: file.name,
        fieldName: 'photos'
      }));

      // Route to asphalt photos if clocked in with ASPHALT labor code
      const uploadType = laborCode === 'ASPHALT' ? 'asphalt_photo' : 'photo';

      // Add to queue (saves immediately, uploads in background)
      await uploadQueueService.addToQueue(
        uploadType,
        { job_id: currentJob.job_id },
        files,
        user.employee_id,
        currentJob.job_id
      );

      // Show success immediately and increment new photos counter
      setUploadSuccess(true);
      setNewPhotosCount(prev => prev + files.length);

      setTimeout(() => {
        setUploadSuccess(false);
        // Don't auto-reload - let user take more photos
      }, 2000);

      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error queueing photos:', error);
      alert('Failed to queue photos for upload');
    } finally {
      setUploading(false);
    }
  };

  const handleTakePhoto = async () => {
    if (!currentJob || !user || cameraCooldown) return;

    // iOS: Use native camera (no permission prompts)
    // Android: Use custom camera (clean UI, no focus ring)
    if (isIOS()) {
      try {
        setCameraCooldown(true);
        // Route to asphalt photos if clocked in with ASPHALT labor code
        const uploadType = laborCode === 'ASPHALT' ? 'asphalt_photo' : 'photo';
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            setUploading(true);
            // Add to queue (saves immediately, uploads in background)
            await uploadQueueService.addToQueue(
              uploadType,
              { job_id: currentJob.job_id },
              [{ file, filename: file.name, fieldName: 'photos' }],
              user.employee_id,
              currentJob.job_id
            );

            // Show success immediately and increment new photos counter
            setUploadSuccess(true);
            setUploading(false);
            setNewPhotosCount(prev => prev + 1);

            setTimeout(() => {
              setUploadSuccess(false);
              // Don't auto-reload - let user take more photos
            }, 2000);
          }
        });

        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (error) {
        console.error('Error capturing photo:', error);
        alert('Failed to capture photo');
        setCameraCooldown(false);
        setUploading(false);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleTakeSiltFencePhoto = async () => {
    if (!currentJob || !user || cameraCooldown) return;

    // iOS: Use native camera (no permission prompts)
    // Android: Use custom camera (clean UI, no focus ring)
    if (isIOS()) {
      try {
        setCameraCooldown(true);
        // Route to asphalt photos if clocked in with ASPHALT labor code
        const uploadType = laborCode === 'ASPHALT' ? 'asphalt_photo' : 'photo';
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            setUploading(true);
            // Add to queue with silt fence caption
            await uploadQueueService.addToQueue(
              uploadType,
              { job_id: currentJob.job_id, caption: 'Silt Fence Inspection' },
              [{ file, filename: file.name, fieldName: 'photos' }],
              user.employee_id,
              currentJob.job_id
            );

            // Show success immediately and increment new photos counter
            setUploadSuccess(true);
            setUploading(false);
            setNewPhotosCount(prev => prev + 1);

            setTimeout(() => {
              setUploadSuccess(false);
            }, 2000);
          }
        });

        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (error) {
        console.error('Error capturing silt fence photo:', error);
        setCameraCooldown(false);
        setUploading(false);
      }
    } else {
      // Android: Set silt fence flag and show camera
      setSiltFenceMode(true);
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    if (!currentJob || !user) return;

    setShowCamera(false);
    const wasSiltFenceMode = siltFenceMode;
    setSiltFenceMode(false); // Reset mode immediately

    try {
      setUploading(true);
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Add caption if this was a silt fence photo
      const caption = wasSiltFenceMode ? 'Silt Fence Inspection' : undefined;

      // Route to asphalt photos if clocked in with ASPHALT labor code
      const uploadType = laborCode === 'ASPHALT' ? 'asphalt_photo' : 'photo';

      // Add to queue (saves immediately, uploads in background)
      await uploadQueueService.addToQueue(
        uploadType,
        { job_id: currentJob.job_id, caption },
        [{ file, filename: file.name, fieldName: 'photos' }],
        user.employee_id,
        currentJob.job_id
      );

      // Show success immediately and increment new photos counter
      setUploadSuccess(true);
      setUploading(false);
      setNewPhotosCount(prev => prev + 1);

      setTimeout(() => {
        setUploadSuccess(false);
        // Don't auto-reload - let user take more photos
      }, 2000);

      // Cooldown
      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (error) {
      console.error('Error capturing photo:', error);
      alert('Failed to capture photo');
      setCameraCooldown(false);
      setUploading(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setSiltFenceMode(false); // Reset silt fence mode on close
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Swipe gesture handling
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      navigatePhoto('next');
    } else if (isRightSwipe) {
      navigatePhoto('prev');
    }
  };

  // Navigate to next/previous photo
  const navigatePhoto = (direction: 'next' | 'prev') => {
    if (!selectedPhoto) return;

    const currentIndex = photos.findIndex(p => p.photo_id === selectedPhoto.photo_id);
    let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    if (newIndex >= 0 && newIndex < photos.length) {
      setImageLoading(true);
      setSelectedPhoto(photos[newIndex]);

      // Preload adjacent images
      if (newIndex > 0) {
        const prevImg = new Image();
        prevImg.src = photos[newIndex - 1].photo_url;
      }
      if (newIndex < photos.length - 1) {
        const nextImg = new Image();
        nextImg.src = photos[newIndex + 1].photo_url;
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedPhoto) return;

      if (e.key === 'ArrowLeft') {
        navigatePhoto('prev');
      } else if (e.key === 'ArrowRight') {
        navigatePhoto('next');
      } else if (e.key === 'Escape') {
        setSelectedPhoto(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedPhoto, photos]);

  // Handle browser back button when photo is open
  useEffect(() => {
    if (selectedPhoto) {
      // Push a state when photo opens
      window.history.pushState({ photoModal: true }, '');

      const handlePopState = (e: PopStateEvent) => {
        setSelectedPhoto(null);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [selectedPhoto]);

  // Delete photo
  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/daily-reports/photos/${photoToDelete.photo_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        // Remove photo from list
        setPhotos(photos.filter(p => p.photo_id !== photoToDelete.photo_id));
        // Close modal if this photo was selected
        if (selectedPhoto?.photo_id === photoToDelete.photo_id) {
          setSelectedPhoto(null);
        }
        setShowDeleteConfirm(false);
        setPhotoToDelete(null);
      } else {
        alert('Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo');
    } finally {
      setDeleting(false);
    }
  };

  // Refresh gallery and reset new photos counter
  const handleRefreshGallery = () => {
    setNewPhotosCount(0);
    loadPhotos();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Loading overlay while initializing
  const isInitializing = loadingStates.user || loadingStates.jobs || loadingStates.currentJob || loadingStates.initializing;
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {language === 'en' ? 'Loading...' : 'Cargando...'}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not clocked in check - different UI for admin vs workers/foremen
  if (!currentJob) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <ImageIcon size={24} />
            {t.title}
          </h1>
        </div>
        <div style={styles.emptyState}>
          {isAdmin ? (
            // Admin: Show JobSelector
            <div>
              <p style={{ marginBottom: '20px', textAlign: 'center' }}>
                {language === 'en'
                  ? 'Please select a job to view photos'
                  : 'Por favor seleccione un trabajo para ver fotos'}
              </p>
              <JobSelector
                jobs={allJobs}
                currentJob={currentJob}
                onJobSelect={(job) => {
                  setCurrentJob(job);
                }}
                mode="admin"
                language={language}
              />
            </div>
          ) : (
            // Workers/Foremen: Must clock in first
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '20px' }}>{t.pleaseClock}</p>
              <button
                onClick={navigateToTimeClock}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {language === 'en' ? 'Go to Time Clock' : 'Ir al Reloj'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <ImageIcon size={24} />
          {t.title}
        </h1>
        <p style={styles.subtitle}>
          {currentJob.job_name}{currentJob.bradanna_job_number ? ` (#${currentJob.bradanna_job_number})` : ''}
        </p>
      </div>

      {/* Job Selector (Admin only) */}
      {user && user.role === 'admin' && (
        <div style={{ position: 'relative' }}>
          <div
            style={styles.jobSelector}
            onClick={() => setShowJobSelector(!showJobSelector)}
          >
            <span style={styles.jobSelectorText}>
              {currentJob ? `${currentJob.job_name}${currentJob.bradanna_job_number ? ` (#${currentJob.bradanna_job_number})` : ''}` : t.selectJob}
            </span>
            <ChevronLeft
              size={20}
              style={{
                transform: showJobSelector ? 'rotate(-90deg)' : 'rotate(90deg)',
                transition: 'transform 0.2s'
              }}
            />
          </div>

          {showJobSelector && (
            <div style={styles.jobDropdown}>
              <input
                type="text"
                placeholder={t.searchJobs}
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={styles.jobSearchInput}
              />
              {allJobs
                .filter(job =>
                  job.job_name.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                  job.bradanna_job_number?.toLowerCase().includes(jobSearchTerm.toLowerCase())
                )
                .map(job => (
                  <div
                    key={job.job_id}
                    style={{
                      ...styles.jobOption,
                      backgroundColor: currentJob?.job_id === job.job_id ? '#f0f9ff' : 'transparent'
                    }}
                    onClick={() => {
                      setCurrentJob(job);
                      setShowJobSelector(false);
                      setJobSearchTerm(''); // Clear search when selected
                      setPage(1); // Reset to first page
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                    onMouseOut={(e) => {
                      if (currentJob?.job_id !== job.job_id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{job.job_name}</div>
                    {job.bradanna_job_number && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        #{job.bradanna_job_number}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {uploadSuccess && (
        <div style={styles.successMessage}>
          ✓ {t.photosSaved}
        </div>
      )}

      {/* Queue Status Indicator */}
      {queueStats.total > 0 && (
        <div style={{
          backgroundColor: queueStats.failed > 0 ? '#fef2f2' : '#f0f9ff',
          border: `2px solid ${queueStats.failed > 0 ? '#fee2e2' : '#dbeafe'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {queueStats.uploading > 0 ? (
            <Cloud size={20} className="animate-pulse" style={{ color: '#3b82f6' }} />
          ) : queueStats.failed > 0 ? (
            <CloudOff size={20} style={{ color: '#ef4444' }} />
          ) : (
            <Cloud size={20} style={{ color: '#10b981' }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>
              {queueStats.uploading > 0 && `${t.uploading} ${queueStats.uploading} ${queueStats.uploading > 1 ? t.photos : t.photo}...`}
              {queueStats.pending > 0 && !queueStats.uploading && `${queueStats.pending} ${queueStats.pending > 1 ? t.photos : t.photo} ${t.queued}`}
              {queueStats.failed > 0 && ` ${queueStats.failed} ${t.failed}`}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {t.uploadAuto}
            </div>
          </div>
          {queueStats.failed > 0 && (
            <button
              onClick={async () => {
                if (confirm('Clear all failed uploads?')) {
                  const { uploadQueueDB } = await import('../../db/upload-queue.db');
                  const failed = await uploadQueueDB.uploads.where('status').equals('failed').toArray();
                  for (const upload of failed) {
                    if (upload.id) {
                      await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
                      await uploadQueueDB.uploads.delete(upload.id);
                    }
                  }
                  const stats = await uploadQueueService.getQueueStats();
                  setQueueStats(stats);
                }
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Clear Failed
            </button>
          )}
        </div>
      )}

      {/* Upload/Camera Section */}
      {user && ['foreman', 'admin'].includes(user.role) && (
        <div style={styles.uploadSection}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
            id="photo-upload"
          />
          <div style={styles.buttonGroup}>
            <button
              onClick={handleTakePhoto}
              disabled={uploading || cameraCooldown || !currentJob}
              style={{
                ...styles.cameraButton,
                opacity: (uploading || cameraCooldown || !currentJob) ? 0.5 : 1,
                cursor: (uploading || cameraCooldown || !currentJob) ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? (
                <>
                  <Upload size={20} className="animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  <Camera size={20} />
                  {t.takePhoto}
                </>
              )}
            </button>
            <label htmlFor="photo-upload" style={{ flex: 1 }}>
              <div style={styles.uploadButton}>
                {uploading ? (
                  <>
                    <Upload size={20} className="animate-spin" />
                    {t.saving}
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    {t.upload}
                  </>
                )}
              </div>
            </label>
          </div>
          {/* Silt Fence Photo Button - Hidden for ASPHALT labor code */}
          {laborCode !== 'ASPHALT' && (
            <button
              onClick={handleTakeSiltFencePhoto}
              disabled={uploading || cameraCooldown || !currentJob}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '14px',
                backgroundColor: uploading || cameraCooldown || !currentJob ? '#d1d5db' : '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: uploading || cameraCooldown || !currentJob ? 'not-allowed' : 'pointer',
                marginTop: '8px',
                opacity: uploading || cameraCooldown || !currentJob ? 0.5 : 1
              }}
            >
              <Fence size={20} />
              {t.siltFencePhoto}
            </button>
          )}
        </div>
      )}

      {/* Search */}
      <div style={styles.searchContainer}>
        <Search size={20} style={styles.searchIcon} />
        <input
          type="text"
          placeholder={t.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1); // Reset to first page on search
          }}
          style={styles.searchInput}
        />
        {searchTerm && (
          <X
            size={20}
            style={styles.clearIcon}
            onClick={() => {
              setSearchTerm('');
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Photo Grid */}
      {loading ? (
        <div style={{
          ...styles.emptyState,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          marginTop: '60px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #4facfe',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{
            fontSize: '18px',
            color: '#64748b',
            fontWeight: '500'
          }}>
            {t.loadingPhotos}
          </div>
        </div>
      ) : photos.length === 0 ? (
        <div style={styles.emptyState}>
          <ImageIcon size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
          <p>{t.noPhotos}</p>
        </div>
      ) : (
        <>
          <div style={styles.galleryGrid}>
            {photos.map((photo) => (
              <div
                key={photo.photo_id}
                style={{ ...styles.photoCard, position: 'relative' as const }}
                onClick={() => {
                  setImageLoading(true);
                  setSelectedPhoto(photo);
                }}
              >
                <img
                  src={photo.thumbnail_url || photo.photo_url}
                  alt={photo.caption || 'Job photo'}
                  style={styles.thumbnail}
                  loading="lazy"
                />
                {/* Silt Fence Badge */}
                {photo.caption === 'Silt Fence Inspection' && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    backgroundColor: '#059669',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    {t.siltFenceTag}
                  </div>
                )}
                <div style={styles.photoInfo}>
                  {formatDate(photo.created_at)}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={page === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
              >
                <ChevronLeft size={16} />
                {t.prev}
              </button>
              <span style={{
                color: '#374151',
                fontWeight: '600',
                fontSize: '16px',
                minWidth: '100px',
                textAlign: 'center'
              }}>
                {t.page} {page} {t.of} {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={page === totalPages ? styles.paginationButtonDisabled : styles.paginationButton}
              >
                {t.next}
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          style={styles.modal}
          onClick={() => setSelectedPhoto(null)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button
            style={styles.closeButton}
            onClick={() => setSelectedPhoto(null)}
          >
            <X size={24} />
          </button>

          {/* Delete Button (Super Admin Only — backend enforces the same, 9/15) */}
          {user && user.is_super_admin === true && (
            <button
              style={styles.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                setPhotoToDelete(selectedPhoto);
                setShowDeleteConfirm(true);
              }}
            >
              <Trash2 size={16} />
              {t.delete}
            </button>
          )}

          {/* Loading spinner overlay */}
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1002
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(255, 255, 255, 0.2)',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          {photos.findIndex(p => p.photo_id === selectedPhoto.photo_id) > 0 && (
            <button
              style={{ ...styles.navButton, ...styles.navButtonLeft, transition: 'transform 0.1s, background-color 0.2s' }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto('prev');
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(0.9)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(1)')}
              onTouchStart={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(0.9)')}
              onTouchEnd={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(1)')}
            >
              <ChevronLeft size={28} />
            </button>
          )}

          {photos.findIndex(p => p.photo_id === selectedPhoto.photo_id) < photos.length - 1 && (
            <button
              style={{ ...styles.navButton, ...styles.navButtonRight, transition: 'transform 0.1s, background-color 0.2s' }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto('next');
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(0.9)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(1)')}
              onTouchStart={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(0.9)')}
              onTouchEnd={(e) => (e.currentTarget.style.transform = 'translateY(-50%) scale(1)')}
            >
              <ChevronRight size={28} />
            </button>
          )}

          <img
            src={selectedPhoto.photo_url}
            alt={selectedPhoto.caption || 'Job photo'}
            style={{
              ...styles.modalImage,
              opacity: imageLoading ? 0.3 : 1,
              transition: 'opacity 0.2s ease-in-out'
            }}
            onClick={(e) => e.stopPropagation()}
            onLoad={() => setImageLoading(false)}
          />
          <div style={styles.modalInfo}>
            <p style={{ fontSize: '16px', marginBottom: '4px' }}>
              {formatDate(selectedPhoto.created_at)}
            </p>
            <p style={{ fontSize: '14px', opacity: 0.8 }}>
              {t.by} {selectedPhoto.uploaded_by_first_name} {selectedPhoto.uploaded_by_last_name}
            </p>
            {selectedPhoto.caption && (
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                {selectedPhoto.caption}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && photoToDelete && (
        <>
          <div style={styles.confirmOverlay} onClick={() => setShowDeleteConfirm(false)} />
          <div style={styles.confirmModal}>
            <h3 style={styles.confirmTitle}>{t.deleteTitle}</h3>
            <p style={styles.confirmText}>
              {t.deleteConfirm} {formatDate(photoToDelete.created_at)}? {t.deleteWarning}
            </p>
            <div style={styles.confirmButtons}>
              <button
                style={styles.confirmButtonCancel}
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPhotoToDelete(null);
                }}
                disabled={deleting}
              >
                {t.cancel}
              </button>
              <button
                style={styles.confirmButtonDelete}
                onClick={handleDeletePhoto}
                disabled={deleting}
              >
                {deleting ? t.deleting : t.deletePhoto}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}

      {/* Floating Action Button (FAB) - Silt Fence (positioned above regular FAB) - Hidden for ASPHALT */}
      {currentJob && !showCamera && laborCode !== 'ASPHALT' && (
        <button
          style={{
            ...styles.fab,
            bottom: '176px',  // Above the main FAB (100px + 64px height + 12px spacing)
            backgroundColor: '#059669'  // Emerald green for silt fence
          }}
          onClick={handleTakeSiltFencePhoto}
          disabled={cameraCooldown || uploading}
          aria-label="Take Silt Fence Photo"
        >
          <Fence size={24} />
        </button>
      )}

      {/* Floating Action Button (FAB) - Camera */}
      {currentJob && !showCamera && (
        <button
          style={styles.fab}
          onClick={handleTakePhoto}
          disabled={cameraCooldown || uploading}
          aria-label="Take Photo"
        >
          <Camera size={28} />
          {newPhotosCount > 0 && (
            <div style={styles.fabBadge}>
              {newPhotosCount}
            </div>
          )}
        </button>
      )}

      {/* Refresh Gallery Button - Shows when new photos exist */}
      {newPhotosCount > 0 && !loading && (
        <button
          style={styles.refreshButton}
          onClick={handleRefreshGallery}
        >
          <Upload size={18} />
          {t.refreshGallery} ({newPhotosCount} {newPhotosCount === 1 ? t.photo : t.newPhotos})
        </button>
      )}

    </div>
  );
};

export default PhotoGallery;
